/**
 * Tells the backend that the member is using the app now, and from where: the
 * CRM's "Ultimo accesso" and "Tipo accesso" (App / WebApp) come from this
 * (client, 21/09/2026).
 *
 * ActivityPingTracker sends it when the app opens, when it comes back to the
 * foreground and as the member moves between screens, at most once every five
 * minutes. Background timers never send it, so an app left open in a tab isn't
 * counted as use.
 *
 * Fire-and-forget, on a route that never answers 401 (POST /api/v1/member-activity),
 * so a ping can't sign the member out. Never sent from a CRM impersonation tab:
 * an operator looking at the app is not the member.
 */
import { Capacitor } from "@capacitor/core";
import config from "../settings/config";
import { getAuthToken, isPerTabSession } from "./authStorage";

const ENDPOINT = "/api/v1/member-activity";

// The server keeps one write per member every five minutes: more is waste.
export const ACTIVITY_PING_INTERVAL_MS = 5 * 60 * 1000;

let lastPing = { userId: null, at: 0 };

export function activityPlatform() {
  try {
    const platform = Capacitor.getPlatform();
    return platform === "ios" || platform === "android" ? platform : "web";
  } catch (_) {
    return "web";
  }
}

function sendPing(token) {
  const baseUrl = config?.env?.[config?.env?.mode]?.url;
  if (!baseUrl) return;

  fetch(`${baseUrl}${ENDPOINT}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ platform: activityPlatform() }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * @returns {boolean} whether a ping went out
 */
export function pingActivity(userId, { now = Date.now(), send = sendPing } = {}) {
  if (!userId) return false;

  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return false;
  }

  if (isPerTabSession()) return false;

  const token = getAuthToken();
  if (!token) return false;

  // A different member signing in on the same device is pinged straight away.
  if (lastPing.userId === userId && now - lastPing.at < ACTIVITY_PING_INTERVAL_MS) {
    return false;
  }

  lastPing = { userId, at: now };
  send(token);

  return true;
}

export function resetActivityPingForTests() {
  lastPing = { userId: null, at: 0 };
}
